import {observer} from 'mobx-react-lite';
import {AdvancedSplitDocumentDialogProps} from '../../../../entity/props/title/advanced-split-document-dialog-props';
import {
  Autocomplete,
  AutocompleteRenderInputParams,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton, LinearProgress,
  Menu,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow, TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DocumentViewer from '../widget/DocumentViewer';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import {KeyValueResource} from '../../../../entity/resource/key-value-resource';
import Skeleton from 'react-loading-skeleton';
import {PageOption} from '../../../../entity/enums/page-option';
import TableSortLabel from '@mui/material/TableSortLabel';
import React, {useEffect, useState} from 'react';
import {Document} from '../../../../entity/resource/document';
import {useService} from '../../../../infrastructure/service/service';
import {SplitDocumentRequest} from '../../../../entity/resource/split-document-request';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import DeleteIcon from '@mui/icons-material/Delete';
import {SplitDocumentDto} from '../../../../entity/dto/split-document-dto';
import ConfirmDialog from '../widget/ConfirmDialog';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import {Form, Formik, FormikHelpers, FormikProps} from 'formik';
import {SplitDocumentRequestDto} from '../../../../entity/dto/split-document-request-dto';
import Grid from '@mui/material/Grid2';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {toast} from 'react-toastify';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import RefreshIcon from '@mui/icons-material/Refresh';

import '../../../../assets/css/style.css';
import '../../../../assets/css/title/split-document.css';

const SplitDocumentDialog = (props: AdvancedSplitDocumentDialogProps): JSX.Element => {
  const {documentService, titleService, sectionService, auditLogsService} = useService();

  const [selectedDocument, setSelectedDocument] = useState<Document>();
  const [documentPageCount, setDocumentPageCount] = useState<number>(0);
  const [documentDetails, setDocumentDetails] = useState<KeyValueResource []>([]);
  const [splitDocumentRequests, setSplitDocumentRequests] = useState<SplitDocumentRequest[]>([]);
  const [menuElement, setMenuElement] = useState<null | HTMLElement>(null);
  const [selectedRequest, setSelectedRequest] = useState<SplitDocumentRequest>();
  const [submitLoading, setSubmitLoading] = useState<boolean>(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [sectionOptions, setSectionOptions] = useState<DropdownOption[]>([]);
  const [progress, setProgress] = useState<number>(0);
  const [message, setMessage] = useState<string>('');
  const [progressRefresh, setProgressRefresh] = useState<boolean>(false);
  const [batchId, setBatchId] = useState<string>('');
  const [lastStateMessage, setLastStateMessage] = useState<string>('');

  const tableHeaders = ['Document Name', 'Section', 'Start Page', 'End Page', ''];

  useEffect(() => {
    const init = async (): Promise<void> => {
      const document = documentService.getDocument();
      setSelectedDocument(document);

      const pageCount = await documentService.getPageCount(document?.url ?? '');
      setDocumentPageCount(pageCount);

      setDocumentDetails([
        { label: 'Type', value: 'PDF'},
        { label: 'Page Count', value : pageCount.toString() }
      ]);

      setSectionOptions(sectionService.getSectionOptions([]));

      if (document) {
        const splitRequests = documentService.getSplitRequests(document.id);
        if (splitRequests.length > 0)
          setSplitDocumentRequests(splitRequests);
      }
    }

    init();
  }, [documentService.getDocument()]);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, splitDocumentRequest: SplitDocumentRequest): void => {
    setMenuElement(event.currentTarget);
    setSelectedRequest(splitDocumentRequest);
  }

  const handleMenuClose = (): void => {
    setMenuElement(null);
  }

  const handleCloseDialog = (): void => {
    titleService.setPageOption(PageOption.DOCUMENTS);
    props.setOpen(false);
  }

  const handleBackButton = (): void => {
    titleService.setPageOption(PageOption.DOCUMENTS);
  }

  const handleDeleteRequest = (): void => {
    handleMenuClose();
    setOpenConfirmDialog(true);
  }

  const deleteRequest = (): void => {
    setSplitDocumentRequests((prevState: SplitDocumentRequest[]): SplitDocumentRequest[] => {
      return prevState.filter((request: SplitDocumentRequest): boolean => request !== selectedRequest);
    });

    setOpenConfirmDialog(false);
  }

  const handleSplitDocument = async (): Promise<void> => {
    setSubmitLoading(true);
    setProgress(5);
    setMessage('Your request is being processed');

    const batchId = crypto.randomUUID();
    const splitDocumentDto: SplitDocumentDto = {
      url: selectedDocument?.url ?? '',
      documentGroups: splitDocumentRequests,
      batchID: batchId
    }
    documentService.splitDocument(props.projectTitle.createdByOrganizationID, props.projectTitle.id, splitDocumentDto);

    await fetchProgress(batchId);
  }

  const fetchProgress = async (batchId: string): Promise<void> => {
    let previousProgress = 0;
    let progressUnchangedDuration = 0;
    const maxUnchangedDuration = 20000;
    const interval = 5000;
    const maxUnchangedCycles = maxUnchangedDuration / interval;

    const intervalId = setInterval(async (): Promise<void> => {
      const response = await auditLogsService.getSplitDocumentsLogs(props.projectTitle.createdByOrganizationID, batchId);
      if (response.progress > progress)
        setProgress(response.progress);

      setMessage(response.message);

      if (response.progress === 90) {
        setSubmitLoading(false);
        setProgress(100);
        setMessage('Document split successfully !!');
        toast.success('Document split successfully !!');

        await titleService.getTitleById(props.projectTitle.createdByOrganizationID, props.projectTitle.id);
        if (selectedDocument)
          documentService.clearSplitRequests(selectedDocument.id);

        titleService.setPageOption(PageOption.DOCUMENTS);
        clearInterval(intervalId);
      } else if (response.progress === 60) {
        toast.error(response.message);
        setSubmitLoading(false);

        setTimeout((): void => props.setOpen(false), 1000);
        clearInterval(intervalId);
      }

      if (response.progress !== previousProgress) {
        previousProgress = response.progress;
        progressUnchangedDuration = 0;
      } else
        progressUnchangedDuration++;

      if (progressUnchangedDuration >= maxUnchangedCycles) {
        setSubmitLoading(false);
        setProgressRefresh(true);
        setBatchId(batchId);
        setLastStateMessage(response.message);

        setMessage('Splitting your PDFs. Please refresh to check the progress.');
        toast.info('Splitting your PDFs. Please refresh to check the progress.');
        clearInterval(intervalId);
      }
    }, interval);
  }

  const handleProgressRefresh = async (): Promise<void> => {
    setProgressRefresh(false);
    setMessage(lastStateMessage);
    setProgress((prevState: number): number => prevState + 1);

    await fetchProgress(batchId);
  }

  const handleDocumentTypeChange = (event: React.SyntheticEvent<Element, Event>, newValue: DropdownOption | null, handleChange: any): void => {
    handleChange({ target: { name: 'documentType', value: newValue?.value || '' }});
  }

  const handleValidate = (values: SplitDocumentRequestDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (values.name === '')
      errors.name = 'Document name is required.';

    if (values.documentType === '')
      errors.documentType = 'Document type is required.';

    if (values.startPage === 0)
      errors.startPage = 'Start page is required.';

    if (values.startPage > values.endPage)
      errors.startPage = 'Start page should less than or equal end page';

    if (values.startPage > documentPageCount)
      errors.startPage = 'Start page should not be grater than page count';

    if (values.endPage === 0)
      errors.endPage = 'End page is required.';

    if (values.endPage < values.startPage)
      errors.endPage = 'End page should grater than or equal start page';

    if (values.endPage > documentPageCount)
      errors.endPage = 'End page should not be grater than page count';

    return errors;
  }

  const handleSubmit = (values: SplitDocumentRequestDto, {setSubmitting, resetForm}: FormikHelpers<SplitDocumentRequestDto>): void => {
    setSubmitting(true);

    const splitRequest: SplitDocumentRequest = {
      fileName: values.name,
      documentType: values.documentType,
      pages: documentService.getSplitPages(values.startPage, values.endPage)
    }

    setSplitDocumentRequests((prevRequests: SplitDocumentRequest[]): SplitDocumentRequest[] => [...prevRequests, splitRequest]);
    if (selectedDocument)
      documentService.addSplitRequest(selectedDocument.id, splitRequest);

    setSubmitting(false);
    resetForm();

    toast.success('New split document request added !!');
  }

  const renderTableRow = (splitDocumentRequest: SplitDocumentRequest, index: number): JSX.Element => {
    return (
      <TableRow
        hover
        role='checkbox'
        tabIndex={-1}
        key={index}
        className={'table-cell'}>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{splitDocumentRequest.fileName}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{splitDocumentRequest.documentType}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{splitDocumentRequest.pages.length > 0 ? splitDocumentRequest.pages[0] : 0}</TableCell>
        <TableCell className={'no-wrap table-cell-text'} align={'left'}>{splitDocumentRequest.pages.length > 0 ? splitDocumentRequest.pages[splitDocumentRequest.pages.length - 1] : 0}</TableCell>
        <TableCell className={'no-wrap'} padding="checkbox">
          <IconButton
            className={'zitles-menu-trigger'}
            color= "inherit"
            disableRipple
            disableFocusRipple
            onClick={(event: React.MouseEvent<HTMLElement>): void => handleMenuOpen(event, splitDocumentRequest)}>
            <MoreHorizIcon />
          </IconButton>
          <Menu elevation={0} anchorEl={menuElement} open={Boolean(menuElement)} className={'zitles-menu'} onClose={handleMenuClose}>
            <MenuItem disableRipple className={'zitles-menu-item'} onClick={handleDeleteRequest}>
              <DeleteIcon className={'zitles-icon'}/>
              <Typography className={'zitles-menu-item button'}>Delete</Typography>
            </MenuItem>
          </Menu>
        </TableCell>
      </TableRow>
    );
  }

  return (
    <>
      <Dialog open={props.open} PaperProps={{style: {width: '100%', height: '95%', maxWidth: 'none', maxHeight: 'none'}}}>
        <DialogTitle className={'zitles-dialog-title'}>
          <span>Advanced Split Document</span>
          <IconButton onClick={handleCloseDialog}>
            <CloseIcon className={'dialog-icon'}/>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box display='flex' height='100%' width='100%'>
            <Box flex={1} overflow='none' padding={2}>
              <DocumentViewer tab={true} />
            </Box>
            <Box flex={1} overflow='none' padding={2}>
              <Card elevation={0} className={'split-document-card'}>
                <CardContent>
                  <Box className={'split-document-header'}>
                    <KeyboardArrowLeftIcon cursor={'pointer'} onClick={handleBackButton}/>
                    <PictureAsPdfIcon className='zitles-icon'/>
                    <Typography className={'split-document-name'}>{selectedDocument?.name ?? ''}</Typography>
                  </Box>
                  <Box className={'split-document-body'}>
                    {
                      documentDetails.map((data: KeyValueResource, index: number): JSX.Element =>
                        <Box key={index} className={'template-item split-document-details'}>
                          <Typography className={'template-data-label user'}>{data.label}</Typography>
                          <Typography className={'template-data-value'}>{data.value ?? <Skeleton/>}</Typography>
                        </Box>
                      )
                    }
                    <Box className='split-form-box'>
                      <Formik
                        initialValues={documentService.getSplitRequest()}
                        validate={handleValidate}
                        onSubmit={handleSubmit}>
                        {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting}: FormikProps<SplitDocumentRequestDto>): JSX.Element => (
                          <Form onSubmit={handleSubmit}>
                            <Grid container spacing={2}>
                              <Grid size={6}>
                                <TextField
                                  required
                                  fullWidth
                                  label='Document Name'
                                  name='name'
                                  value={values.name}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.name && Boolean(errors.name)}
                                  helperText={touched.name && errors.name}/>
                              </Grid>
                              <Grid size={6}>
                                <Autocomplete
                                  options={sectionOptions}
                                  getOptionLabel={(option: DropdownOption): string => option.text}
                                  value={sectionOptions.find((option: DropdownOption): boolean => option.value === values.documentType) || null}
                                  onChange={(event: React.SyntheticEvent<Element, Event>, newValue: DropdownOption | null): void => handleDocumentTypeChange(event, newValue, handleChange)}
                                  onBlur={handleBlur}
                                  renderInput={(params: AutocompleteRenderInputParams): JSX.Element => (
                                    <TextField
                                      {...params}
                                      name='documentType'
                                      label='Document Type'
                                      error={touched.documentType && Boolean(errors.documentType)}
                                      helperText={touched.documentType && errors.documentType}
                                      required
                                      fullWidth/>
                                  )}/>
                              </Grid>
                              <Grid size={6}>
                                <TextField
                                  required
                                  type={'number'}
                                  fullWidth
                                  label='Start Page'
                                  name='startPage'
                                  value={values.startPage}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.startPage && Boolean(errors.startPage)}
                                  helperText={touched.startPage && errors.startPage}/>
                              </Grid>
                              <Grid size={6}>
                                <TextField
                                  required
                                  type={'number'}
                                  fullWidth
                                  label='End Page'
                                  name='endPage'
                                  value={values.endPage}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  error={touched.endPage && Boolean(errors.endPage)}
                                  helperText={touched.endPage && errors.endPage}/>
                              </Grid>
                            </Grid>
                            <Box className={'action-btn-container split-form-btn'}>
                              <Button
                                type='submit'
                                disabled={isSubmitting || submitLoading}
                                className={`zitles-button submit ${(isSubmitting || submitLoading) ? 'disable' : ''}`}
                                startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
                                {
                                  isSubmitting ? 'Adding' : 'Add Request'
                                }
                              </Button>
                            </Box>
                          </Form>
                        )}
                      </Formik>
                    </Box>
                    <Box>
                      <Paper className={'table-container'} component={Paper} elevation={0}>
                        {
                          splitDocumentRequests.length > 0
                            ? <TableContainer className={'zitles-table split-table'}>
                                <Table>
                                  <TableHead>
                                    <TableRow className={'table-header'}>
                                      {
                                        tableHeaders.map((header: string): JSX.Element =>
                                          <TableCell key={header} className={'no-wrap'} align='left' padding='normal'>
                                            <TableSortLabel className={'table-header-text'} active={false} direction='desc'>{header}</TableSortLabel>
                                          </TableCell>
                                        )
                                      }
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {splitDocumentRequests.map(renderTableRow)}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            : <Typography className='empty-message'>Please click add button to submit split requests !!</Typography>
                        }
                      </Paper>
                      {
                        splitDocumentRequests.length > 0 && progress === 0 &&
                          <Button
                            disableElevation
                            className={`zitles-button submit split-button ${submitLoading ? 'disable' : ''}`}
                            variant="contained"
                            onClick={handleSplitDocument}
                            startIcon={submitLoading ? <CircularProgress size={14} color="inherit"/> : null}>
                            {submitLoading ? 'Splitting' : 'Split Document'}
                          </Button>
                      }
                      {
                        progress > 0 &&
                          <Box className={'progress-zone'}>
                            <Box className={'progress-icon-zone'}>
                              {
                                progressRefresh
                                  ? <RefreshIcon className={'progress-icon'} cursor={'pointer'} onClick={handleProgressRefresh}/>
                                  : <InsertDriveFileIcon className={'progress-icon'}/>
                              }
                            </Box>
                            <Box className={'progress-bar-zone'}>
                              <Typography className={'progress-bar-title'}>{message}</Typography>
                              <Box className={'progress-bar-container'}>
                                <Box className={'progress-bar'}>
                                  <LinearProgress variant="determinate" {...props} value={progress} color="success" className={'progress'}/>
                                </Box>
                                <Typography className={'progress-value'}>{`${progress}%`}</Typography>
                              </Box>
                            </Box>
                          </Box>
                      }
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      {
        openConfirmDialog &&
          <ConfirmDialog
            open={openConfirmDialog}
            setOpen={setOpenConfirmDialog}
            title={'Delete Split Request'}
            message={'Are you sure you want to delete this split request?'}
            handleSubmit={deleteRequest}
            loading={false}/>
      }
    </>
  );
}

export default observer(SplitDocumentDialog);
